/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

import { Link, navigate, WrapRootElementBrowserArgs } from "gatsby";
import React from "react";

import { navigator } from "@pray/shared/utils/navigation";

import { captureUtmParams } from "./src/utils/utmTracking";

import "./src/styles/global.css";

// add global styles from shared library
import "@pray/shared/styles/theme.scss";
import "@pray/shared/styles/normalize.css";
import "@pray/shared/styles/global.css";

export function onClientEntry() {
  window.history.scrollRestoration = "manual";
  navigator.initialize({ navigate, Link });

  // Capture UTM params from the landing URL for conversion attribution.
  captureUtmParams(window.location.search);

  // Detect when fonts are loaded to prevent FOUT on hero title
  function markFontsLoaded() {
    document.documentElement.classList.add("fonts-loaded");
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(markFontsLoaded).catch(markFontsLoaded);
  } else {
    // Fallback for browsers without Font Loading API
    markFontsLoaded();
  }
}

export function shouldUpdateScroll() {
  return false;
}

export function onRouteUpdate({
  location,
  prevLocation,
}: {
  location: Location;
  prevLocation: Location | null;
}) {
  // Re-capture UTMs in case the session enters on a tagged non-landing route.
  captureUtmParams(location.search);

  // If there's a hash in the URL, scroll to that element
  if (location.hash) {
    const id = location.hash.replace("#", "");

    const scrollToElement = () => {
      const element = document.getElementById(id);
      if (element) {
        // Get element position and scroll with offset for navigation
        const elementRect = element.getBoundingClientRect();
        const absoluteTop = window.scrollY + elementRect.top;
        const navOffset = 80; // Account for fixed navigation
        window.scrollTo({
          top: absoluteTop - navOffset,
          left: 0,
          behavior: "instant",
        });
      }
    };

    // Wait for page to fully render before scrolling
    setTimeout(scrollToElement, 150);
    return;
  }

  // Otherwise scroll to top instantly (bypass smooth scroll CSS)
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });

  // Track pageview in GA4 (cookieless mode — no consent banner needed)
  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_location: location.href,
      page_path: location.pathname,
    });
  }

  // Meta Pixel SPA pageview. The initial PageView already fires from the base
  // code in gatsby-ssr.tsx on first load (prevLocation === null), so only fire
  // here on client-side route changes — otherwise the landing page double-counts.
  if (typeof window.fbq === "function") {
    if (prevLocation) {
      window.fbq("track", "PageView");
    }

    // Fire ViewContent on the landing page only — per the scope's
    // "content-view event on landing".
    if (location.pathname === "/" || location.pathname === "") {
      window.fbq("track", "ViewContent");
    }
  }
}

export function wrapRootElement({ element }: WrapRootElementBrowserArgs) {
  return element;
}
